import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { StoreSubscriptionPaymentReviewStatus } from "@/lib/api/types";
import { STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS } from "@/lib/api/types";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useWalletTopUpRequestsQuery } from "@/modules/subscriptions/services/useWalletTopUpRequestsQuery";
import { Alert, Button, EmptyView, ErrorState, Icon, LoadingState, SegmentedControl } from "@/shared/rd";
import { TopUpRequestCard } from "../components/TopUpRequestCard";

type WalletTopUpRequestsPageProps = {
  locale: Locale;
  reviewStatus: StoreSubscriptionPaymentReviewStatus;
  limit: number;
  offset: number;
  /** From the admin notification's `data.paymentId` — the row to point at. */
  highlightedPaymentId: string;
  /** From the same notification, used only for the "not in this filter" escape hatch. */
  highlightedStoreId: string;
};

const WALLET_TOP_UP_REQUESTS_PATH = "wallet-top-up-requests";

function buildQuery(params: {
  reviewStatus: StoreSubscriptionPaymentReviewStatus;
  limit: number;
  offset: number;
  paymentId?: string;
  storeId?: string;
}) {
  const query = new URLSearchParams();
  query.set("reviewStatus", params.reviewStatus);
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.paymentId) {
    query.set("paymentId", params.paymentId);
  }
  if (params.storeId) {
    query.set("storeId", params.storeId);
  }
  return query.toString();
}

/**
 * The cross-store wallet top-up review queue.
 *
 * Before this screen a pending request was only reachable from inside its own
 * store's subscription tab, so finding work meant already knowing which store
 * was waiting. The default filter is therefore PENDING: that is the queue, and
 * the other two statuses are the audit trail.
 *
 * Confirm and reject hit the same store-scoped routes as the store panel
 * (the shared mutations), with the `storeId` and `id` every row carries.
 */
export function WalletTopUpRequestsPage({
  locale,
  reviewStatus,
  limit,
  offset,
  highlightedPaymentId,
  highlightedStoreId,
}: WalletTopUpRequestsPageProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const basePath = `/${locale}/admin/${WALLET_TOP_UP_REQUESTS_PATH}`;
  const { data: session } = useSessionQuery();
  const { data, isLoading, error, refetch } = useWalletTopUpRequestsQuery({
    reviewStatus,
    limit,
    offset,
  });
  const highlightedRef = useRef<HTMLElement | null>(null);

  // An admin arriving from a push notification should not have to hunt the row
  // down in a list of twenty; bring it to them once the list has rendered.
  const items = data?.items;
  useEffect(() => {
    if (!highlightedPaymentId || !items || !highlightedRef.current) {
      return;
    }
    highlightedRef.current.scrollIntoView({ block: "center" });
  }, [highlightedPaymentId, items]);

  const pageName = t(keys.admin.subscriptionTopUpRequestsTitle).toLocaleLowerCase(locale);

  if (isLoading) {
    return (
      <LoadingState
        title={t(keys.shell.loadingTitle, { page: pageName })}
        sub={t(keys.shell.loadingSub)}
        rows={Math.min(limit, 6)}
      />
    );
  }

  if (error || !data) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: pageName })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={t(keys.shell.retry)}
      />
    );
  }

  /**
   * Confirm and reject are managing-admin work (`MANAGING_ADMIN_ROLES` on the
   * backend — the same set the "a seller is waiting on money" notification is
   * sent to). A CUSTOMER_SERVICE or SALES admin can read the queue, but is not
   * shown two buttons whose whole purpose is to move money.
   */
  const canReview =
    session?.admin?.roles?.some((role) =>
      [ADMIN_ROLES.ADMIN as string, ADMIN_ROLES.SUPER_ADMIN as string].includes(role),
    ) ?? false;

  const link = (next: { reviewStatus?: StoreSubscriptionPaymentReviewStatus; offset: number }) =>
    `${basePath}?${buildQuery({
      reviewStatus: next.reviewStatus ?? reviewStatus,
      limit,
      offset: next.offset,
      paymentId: highlightedPaymentId || undefined,
      storeId: highlightedStoreId || undefined,
    })}`;

  const hasPrev = offset > 0;
  const hasNext = offset + limit < data.total;
  const page = Math.floor(offset / limit) + 1;
  const isHighlightMissing =
    Boolean(highlightedPaymentId) && !data.items.some((item) => item.id === highlightedPaymentId);

  return (
    <>
      <Alert tone="info">{t(keys.admin.subscriptionTopUpRequestsHint)}</Alert>

      <div className="rd-toolbar" style={{ gap: 12 }}>
        <span style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
          {t(keys.admin.walletTopUpQueueFilterStatus)}
        </span>
        <SegmentedControl<StoreSubscriptionPaymentReviewStatus>
          label={t(keys.admin.walletTopUpQueueFilterStatus)}
          value={reviewStatus}
          onChange={(value) => navigate(link({ reviewStatus: value, offset: 0 }))}
          options={[
            {
              value: STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.PENDING,
              label: t(keys.admin.subscriptionTopUpStatusPending),
            },
            {
              value: STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.CONFIRMED,
              label: t(keys.admin.subscriptionTopUpStatusConfirmed),
            },
            {
              value: STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.REJECTED,
              label: t(keys.admin.subscriptionTopUpStatusRejected),
            },
          ]}
        />
        <div className="rd-spacer" />
        <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
          {t("admin.walletTopUpQueueCount", { count: data.total })}
        </span>
      </div>

      {isHighlightMissing ? (
        <Alert
          tone="warn"
          action={
            highlightedStoreId ? (
              <Link
                to={`/${locale}/admin/stores/${highlightedStoreId}?tab=subscriptions`}
                style={{ color: "var(--rd-primary)", fontWeight: 500, whiteSpace: "nowrap" }}
              >
                {t(keys.admin.walletTopUpQueueOpenStore)}
              </Link>
            ) : undefined
          }
        >
          {t(keys.admin.walletTopUpQueueNotInFilter)}
        </Alert>
      ) : null}

      {data.items.length === 0 ? (
        <div className="rd-card">
          {reviewStatus === STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.PENDING ? (
            <EmptyView
              title={t("admin.walletTopUpQueuePendingEmptyTitle")}
              sub={t("admin.walletTopUpQueuePendingEmptySub")}
            />
          ) : (
            <EmptyView title={t(keys.admin.walletTopUpQueueEmpty)} />
          )}
        </div>
      ) : (
        data.items.map((item) => {
          const isHighlighted = item.id === highlightedPaymentId;
          return (
            <TopUpRequestCard
              key={item.id}
              ref={isHighlighted ? highlightedRef : undefined}
              item={item}
              locale={locale}
              highlighted={isHighlighted}
              canReview={canReview}
            />
          );
        })
      )}

      <nav
        aria-label={t(keys.common.paginationNav)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, color: "var(--rd-text-tertiary)" }}
      >
        <Button
          variant="ghost"
          size="sm"
          icon={<Icon icon={ChevronLeft} size={14} className="rd-flip-rtl" />}
          disabled={!hasPrev}
          onClick={() => navigate(link({ offset: Math.max(offset - limit, 0) }))}
        >
          {t(keys.common.previous)}
        </Button>
        <span className="rd-num">{t(keys.common.pageNumber, { page })}</span>
        <Button
          variant="ghost"
          size="sm"
          iconEnd={<Icon icon={ChevronRight} size={14} className="rd-flip-rtl" />}
          disabled={!hasNext}
          onClick={() => navigate(link({ offset: offset + limit }))}
        >
          {t(keys.common.next)}
        </Button>
      </nav>
    </>
  );
}
